import {
  AccountCategory,
  activeSourceCurrenciesAtom,
  CashInfo,
  getFormatter,
  isDefined,
  sourceCurrenciesAtom,
  useDictionary,
  when,
} from '@eih-netbroker/common';
import { useAtomValue } from 'jotai/index';
import { useCallback, useEffect, useMemo } from 'react';
import { ReactComponent as DepositIcon } from '../../../../assets/icons/add-line.svg';
import { ReactComponent as AllocationIcon } from '../../../../assets/icons/arrow-right-up-line-v2.svg';
import { ReactComponent as CurrencyExchangeIcon } from '../../../../assets/icons/exchange-loop.svg';
import { ReactComponent as InfoOutlinedIcon } from '../../../../assets/icons/information-line.svg';
import { ReactComponent as RefreshIcon } from '../../../../assets/icons/refresh.svg';
import { ReactComponent as TransferIcon } from '../../../../assets/icons/transfer.svg';
import { useDialog } from '../../../../context/DialogContext';
import { useLocale } from '../../../../context/NbLocalizationContext';
import { NbButton } from '../../../../shared/components/button/Button';
import { NbIconButton } from '../../../../shared/components/icon-button/IconButton';
import { NbIndefiniteLoader } from '../../../../shared/components/loader/Loader';
import { MoneyMaskHOC } from '../../../../shared/components/money-mask/MoneyMask';
import { NbTooltip } from '../../../../shared/components/tooltip/Tooltip';
import { Config } from '../../../../shared/config/config';
import { useAccounts } from '../../../../shared/services/useAccounts';
import { useCurrencyExchangeDialog } from '../../../../shared/services/useCurrencyExchangeDialog';
import { cssWhen } from '../../../../shared/utils/themeUtils';
import { CashAllocationModal } from '../../../cash-action/components/allocation/CashAllocationModal';
import { CashDepositModal } from '../../../cash-action/components/deposit/CashDepositModal';
import { CashTransferModal } from '../../../cash-action/components/transfer/CashTransferModal';
import { usePayment } from '../../../cash-action/services/usePayment';
import { useCollateralSummary } from '../../../portfolio/services/useCollateralSummary';
import classes from './CashPopoverContent.module.scss';
import { CashPopoverListItem, FiatWithExchangePeriodInfo } from './CashPopoverListItem';

export interface CashPopoverContentProps {
  inventoryInHuf: number;
  fiats: CashInfo[];
  handleCashClose: () => void;
  handleCashOnClick: () => void;
}

export const CashPopoverContent = ({
  inventoryInHuf,
  fiats,
  handleCashClose,
  handleCashOnClick,
}: CashPopoverContentProps) => {
  const { translation } = useLocale();
  const { formatCurrency } = getFormatter();
  const { selectedClientName, selectedAccountOrClient, getSelectedAccount } = useAccounts();
  const sourceCurrencies = useAtomValue(sourceCurrenciesAtom);
  const activeSourceCurrencies = useAtomValue(activeSourceCurrenciesAtom);
  const { investmentLoan, loadCollateralSummary } = useCollateralSummary();
  const { formatDictionaryType } = useDictionary();
  const { openDialog } = useDialog();
  const { handleCashActionDialogOpen } = usePayment();

  const openDepositDialog = useCallback(async () => {
    await openDialog((close: () => void) => <CashDepositModal onClose={close} />);
  }, [openDialog]);

  const openTransferDialog = useCallback(async () => {
    await openDialog((close: () => void) => <CashTransferModal onClose={close} />);
  }, [openDialog]);

  const openAllocationDialog = useCallback(async () => {
    await openDialog((close: () => void) => <CashAllocationModal onClose={close} />);
  }, [openDialog]);

  const invalidAccountSelectedForExchange = async () => {
    await getSelectedAccount(false, [AccountCategory.Nyesz]);
    handleCashOnClick();
  };

  const { handleCurrencyExchangeOpen } = useCurrencyExchangeDialog({
    onBeforeOpen: handleCashClose,
    onInvalidAccount: invalidAccountSelectedForExchange,
  });

  useEffect(() => {
    loadCollateralSummary();
  }, [loadCollateralSummary]);

  const fiatsWithExchangePeriodInfo = useMemo(
    () =>
      fiats.map<FiatWithExchangePeriodInfo>(fiat => {
        const ccyIsInSourceCurrencies =
          sourceCurrencies.isLoaded && sourceCurrencies.some(_ => _.currency === fiat.ccy);
        const isExchangePeriod = when<FiatWithExchangePeriodInfo['isExchangePeriod']>(
          activeSourceCurrencies.isLoading,
          'loading',
        )
          // if the currency is in the active source currencies list, it is enabled
          .or(activeSourceCurrencies.isLoaded && activeSourceCurrencies.includes(fiat.ccy), 'active')
          // if it's not in the active source currencies list, but it's in the source currencies list, it's disabled
          .or(
            activeSourceCurrencies.isLoaded && !activeSourceCurrencies.includes(fiat.ccy) && ccyIsInSourceCurrencies,
            'disabled',
          )
          // if it's not in the source currencies list, it's unavailable
          .or(!ccyIsInSourceCurrencies, 'unavailable')
          .otherwise('loading')
          .get();

        return {
          ...fiat,
          isExchangePeriod,
        };
      }),
    [fiats, activeSourceCurrencies, sourceCurrencies],
  );

  return (
    <div className={classes.cash}>
      <div className={classes.cashHeader}>
        <div className={classes.cashHeaderText}>
          <h4 data-hj-suppress>{selectedClientName}</h4>
          <p className={classes.cashHeaderUser}>
            {selectedAccountOrClient?.account?.accountCategory
              ? formatDictionaryType(selectedAccountOrClient.account.accountCategory, 'accountCategoryTypes')
              : translation.header.allAccounts}
          </p>
        </div>
        {selectedAccountOrClient.account?.accountCategory !== AccountCategory.Nyesz && (
          <NbButton
            data-testid="cash-popover-content-exchange-button"
            size={'small'}
            variant={'outlined'}
            onClick={() => handleCurrencyExchangeOpen()}
            className={cssWhen(classes.cashExchangeButton, [
              sourceCurrencies.isLoaded && activeSourceCurrencies.isLoaded && activeSourceCurrencies.length === 0,
              classes.cashExchangeButtonIsDisabled,
            ])}
            isLoading={sourceCurrencies.isLoading || activeSourceCurrencies.isLoading}
            disabled={sourceCurrencies.isLoading || activeSourceCurrencies.hasError}
            loaderSize={12}
          >
            <CurrencyExchangeIcon /> {translation.header.cash.exchange}
          </NbButton>
        )}
      </div>
      {Config.FeatureFlags.isCashActionEnabled && (
        <div className={classes.cashActionsContainer}>
          <NbButton
            data-testid="cash-popover-content-deposit-button"
            size="small"
            color="primary"
            variant="contained"
            startIcon={<DepositIcon />}
            className={classes.cashActionButtonPrimary}
            onClick={() => handleCashActionDialogOpen(openDepositDialog, handleCashClose)}
          >
            {translation.header.cash.deposit}
          </NbButton>
          <NbButton
            data-testid="cash-popover-content-transfer-button"
            size="small"
            color="primary"
            variant="outlined"
            startIcon={<TransferIcon />}
            className={classes.cashActionButtonOutline}
            onClick={() => handleCashActionDialogOpen(openTransferDialog, handleCashClose)}
          >
            {translation.header.cash.transfer}
          </NbButton>
          <NbButton
            data-testid="cash-popover-content-allocation-button"
            size="small"
            color="primary"
            variant="outlined"
            startIcon={<AllocationIcon />}
            className={classes.cashActionButtonOutline}
            onClick={() => handleCashActionDialogOpen(openAllocationDialog, handleCashClose)}
          >
            {translation.header.cash.allocation}
          </NbButton>
        </div>
      )}
      <div className={classes.cashContent}>
        {selectedAccountOrClient.account?.accountCategory !== AccountCategory.Nyesz && (
          <div className={classes.cashRow}>
            <p>{translation.header.cash.total}</p>
            <MoneyMaskHOC
              isInverse
              size={100}
            >
              <p data-id="header-cash-total">{formatCurrency(inventoryInHuf, 'HUF')}</p>
            </MoneyMaskHOC>
          </div>
        )}

        {fiatsWithExchangePeriodInfo.map(fiat => (
          <CashPopoverListItem
            key={fiat.ccy}
            onClick={handleCurrencyExchangeOpen}
            fiatWithExchangePeriodInfo={fiat}
          />
        ))}
      </div>

      {(selectedAccountOrClient.account?.accountCategory === AccountCategory.Normal ||
        isDefined(selectedAccountOrClient.client)) && (
        <div className={classes.cashContent}>
          <div className={cssWhen(classes.cashRow, classes.cashRowIsWithoutBorder, classes.cashRowInvestmentLoan)}>
            {(investmentLoan.isLoaded || investmentLoan.isLoading) && (
              <p>{translation.header.cash.availableInvestmentLoan}</p>
            )}
            {investmentLoan.hasError && (
              <p className={classes.cashRowIsError}>{translation.header.cash.availableInvestmentLoanError}</p>
            )}
            {investmentLoan.hasError && (
              <NbIconButton
                icon={<RefreshIcon />}
                size="small"
                onClick={() => loadCollateralSummary(true)}
              />
            )}
            {investmentLoan.isLoaded && (
              <div
                data-id="header-cash-total"
                className={classes.cashRowInvestmentLoanValue}
              >
                <MoneyMaskHOC
                  isInverse
                  size={100}
                >
                  <span data-id="header-cash-collateral">{formatCurrency(investmentLoan.value, 'HUF', true)}</span>
                  <NbTooltip title={translation.header.cash.availableInvestmentLoanTooltip}>
                    <InfoOutlinedIcon />
                  </NbTooltip>
                </MoneyMaskHOC>
              </div>
            )}
            {investmentLoan.isLoading && (
              <NbIndefiniteLoader
                isContrast
                size={100}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
